import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Calendar, 
  Sprout, 
  ShoppingBag, 
  Info, 
  Filter, 
  Loader2, 
  CheckCircle2, 
  X, 
  Plus, 
  ListChecks, 
  ChevronDown, 
  ChevronUp 
} from 'lucide-react';
import { Language } from '../translations';
import { db } from '../firebase';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { CropSchedule, UserProfile, UserCrop, Practice } from '../types';

const MONTHS = [
  { en: 'January', te: 'జనవరి', hi: 'जनवरी' },
  { en: 'February', te: 'ఫిబ్రవరి', hi: 'फरवरी' },
  { en: 'March', te: 'మార్చి', hi: 'मार्च' },
  { en: 'April', te: 'ఏప్రిల్', hi: 'अप्रैल' },
  { en: 'May', te: 'మే', hi: 'मई' },
  { en: 'June', te: 'జూన్', hi: 'जून' },
  { en: 'July', te: 'జూలై', hi: 'जुलाई' },
  { en: 'August', te: 'ఆగస్టు', hi: 'अगस्त' },
  { en: 'September', te: 'సెప్టెంబర్', hi: 'सितंबर' },
  { en: 'October', te: 'అక్టోబర్', hi: 'अक्टूबर' },
  { en: 'November', te: 'నవంబర్', hi: 'नवंबर' },
  { en: 'December', te: 'డిసెంబర్', hi: 'दिसंबर' }
];

export const CROP_NAME_TRANSLATIONS: Record<string, { en: string; te: string; hi: string }> = {
  bengalgram: { en: 'Bengalgram', te: 'శనగ', hi: 'चना' },
  'bengal gram': { en: 'Bengalgram', te: 'శనగ', hi: 'चना' },
  paddy: { en: 'Paddy', te: 'వరి', hi: 'चावल' },
  maize: { en: 'Maize', te: 'మొక్కజొన్న', hi: 'मक्का' },
  pmds: { en: 'PMDS', te: 'PMDS', hi: 'PMDS' },
  cotton: { en: 'Cotton', te: 'పత్తి', hi: 'कपास' },
  groundnut: { en: 'Groundnut', te: 'వేరుశనగ', hi: 'मूंगफली' },
  redgram: { en: 'Redgram', te: 'కంది', hi: 'अरहर' },
  'red gram': { en: 'Redgram', te: 'కంది', hi: 'अरहर' },
  greengram: { en: 'Greengram', te: 'పెసర', hi: 'मूंग' },
  blackgram: { en: 'Blackgram', te: 'మినుము', hi: 'उड़द' },
  chilli: { en: 'Chilli', te: 'మిర్చి', hi: 'मिर्च' },
  turmeric: { en: 'Turmeric', te: 'పసుపు', hi: 'हल्दी' },
  sugarcane: { en: 'Sugarcane', te: 'చెరకు', hi: 'गन्ना' },
  millets: { en: 'Millets', te: 'చిరుధాన్యాలు', hi: 'बाजरा' },
};

export const getLocalizedCropName = (crop: CropSchedule, lang: Language): string => {
  const direct = (crop[`name_${lang}` as keyof CropSchedule] as string)?.trim();
  if (direct) return direct;

  const enKey = (crop.name_en || '').toLowerCase().trim();
  if (enKey && CROP_NAME_TRANSLATIONS[enKey]?.[lang]) {
    return CROP_NAME_TRANSLATIONS[enKey][lang];
  }

  // Check known translations against existing Telugu/Hindi names
  for (const item of Object.values(CROP_NAME_TRANSLATIONS)) {
    if ((crop.name_en && item.en.toLowerCase() === crop.name_en.toLowerCase().trim()) ||
        (crop.name_te && item.te === crop.name_te.trim()) ||
        (crop.name_hi && item.hi === crop.name_hi.trim())) {
      return item[lang];
    }
  }

  // English fallback only if the user selected English
  if (lang === 'en') {
    return crop.name_en?.trim() || '';
  }

  // Never leak English or wrong language when user selected another language
  return '';
};

export const getLocalizedCropDetails = (crop: CropSchedule, lang: Language): string => {
  const direct = (crop[`details_${lang}` as keyof CropSchedule] as string)?.trim();
  if (direct) return direct;
  if (lang === 'en') return crop.details_en?.trim() || '';
  return '';
};

export const getLocalizedPracticeTitle = (practice: Practice, lang: Language): string => {
  const direct = (practice[`title_${lang}` as keyof Practice] as string)?.trim();
  if (direct) return direct;
  if (lang === 'en') return practice.title_en?.trim() || '';
  return '';
};

export const getLocalizedPracticeDetails = (practice: Practice, lang: Language): string => {
  const direct = (practice[`details_${lang}` as keyof Practice] as string)?.trim();
  if (direct) return direct;
  if (lang === 'en') return practice.details_en?.trim() || '';
  return '';
};

export default function CropCalendar({ 
  language, 
  t, 
  user, 
  profile, 
  onUpdateProfile 
}: { 
  language: Language, 
  t: any, 
  user: any, 
  profile: UserProfile | null,
  onUpdateProfile?: (updates: Partial<UserProfile>) => Promise<void>
}) {
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth());
  const [filterType, setFilterType] = useState<'planting' | 'harvest'>('planting');
  const [crops, setCrops] = useState<CropSchedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [followingCrop, setFollowingCrop] = useState<CropSchedule | null>(null);
  const [editingFollowedCrop, setEditingFollowedCrop] = useState<UserCrop | null>(null);
  const [sowingDate, setSowingDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [expandedCropId, setExpandedCropId] = useState<string | null>(null);

  useEffect(() => {
    const q = query(collection(db, 'cropCalendar'), orderBy('order', 'asc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const cropsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as CropSchedule[];
      setCrops(cropsData);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching crop calendar:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleFollow = async () => {
    if (!followingCrop || !user || !onUpdateProfile) return;

    const localizedName = getLocalizedCropName(followingCrop, language);
    const newFollowedCrop: UserCrop = {
      cropId: followingCrop.id,
      cropName: localizedName || followingCrop.name_en,
      sowingDate: sowingDate,
      completedPractices: []
    };

    const currentFollowed = profile?.followedCrops || [];
    const updatedFollowed = [...currentFollowed.filter(c => c.cropId !== followingCrop.id), newFollowedCrop];

    try {
      await onUpdateProfile({ followedCrops: updatedFollowed });
      setFollowingCrop(null);
    } catch (error) {
      console.error("Error following crop:", error);
    }
  };

  const handleUpdateSowingDate = async () => {
    if (!editingFollowedCrop || !user || !onUpdateProfile || !profile) return;

    const updatedFollowed = (profile.followedCrops || []).map(c => 
      c.cropId === editingFollowedCrop.cropId 
        ? { ...c, sowingDate: sowingDate }
        : c
    );

    try {
      await onUpdateProfile({ followedCrops: updatedFollowed });
      setEditingFollowedCrop(null);
    } catch (error) {
      console.error("Error updating sowing date:", error);
    }
  };

  const handleUnfollow = async (cropId: string) => {
    if (!user || !onUpdateProfile || !profile) return;
    const confirmMessage = t.unfollowCropConfirm || (
      language === 'te' 
        ? 'మీరు ఈ పంటను అనుసరించడం ఆపాలనుకుంటున్నారా?' 
        : language === 'hi' 
          ? 'क्या आप इस फसल को अनफ़ॉलो करना चाहते हैं?' 
          : 'Are you sure you want to stop following this crop?'
    );
    if (!window.confirm(confirmMessage)) return;

    const updatedFollowed = (profile.followedCrops || []).filter(c => c.cropId !== cropId);

    try {
      await onUpdateProfile({ followedCrops: updatedFollowed });
    } catch (error) {
      console.error("Error unfollowing crop:", error);
    }
  };

  const isFollowing = (cropId: string) => {
    return profile?.followedCrops?.some(c => c.cropId === cropId);
  };

  // Only crops available and localized in the user's selected language appear in the calendar
  const filteredCrops = crops.filter(crop => {
    const localizedName = getLocalizedCropName(crop, language);
    if (!localizedName) return false;

    return filterType === 'planting' 
      ? (crop.plantingMonths || []).includes(selectedMonth)
      : (crop.harvestMonths || []).includes(selectedMonth);
  });

  const getDayOffsetLabel = (days: number) => {
    if (language === 'te') return `${days}వ రోజు`;
    if (language === 'hi') return `दिन ${days}`;
    return `Day ${days}`;
  };

  return (
    <div className="p-4 space-y-6 pb-20">
      <div className="flex items-center gap-3 mb-2">
        <div className="bg-[#1b7d36]/10 p-3 rounded-2xl">
          <Calendar className="text-[#1b7d36]" size={24} />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-[#1b7d36]">{t.cropCalendar}</h2>
          <p className="text-stone-500 text-sm">{t.tagline}</p>
        </div>
      </div>

      {/* Month Selector */}
      <div className="bg-white rounded-[32px] p-2 shadow-sm border border-black/5 overflow-x-auto no-scrollbar">
        <div className="flex gap-2 min-w-max p-1">
          {MONTHS.map((month, index) => (
            <button
              key={index}
              onClick={() => setSelectedMonth(index)}
              className={`px-6 py-3 rounded-2xl font-bold transition-all duration-300 ${
                selectedMonth === index 
                  ? 'bg-[#1b7d36] text-white shadow-md scale-105' 
                  : 'text-stone-500 hover:bg-stone-100'
              }`}
            >
              {month[language]}
            </button>
          ))}
        </div>
      </div>

      {/* Filter Toggle */}
      <div className="flex bg-stone-100 p-1 rounded-2xl w-full">
        <button
          onClick={() => setFilterType('planting')}
          className={`flex-1 py-3 rounded-xl font-bold transition-all flex items-center justify-center gap-2 ${
            filterType === 'planting' ? 'bg-white text-[#1b7d36] shadow-sm' : 'text-stone-500'
          }`}
        >
          <Sprout size={18} />
          {t.plantingMonth}
        </button>
        <button
          onClick={() => setFilterType('harvest')}
          className={`flex-1 py-3 rounded-xl font-bold transition-all flex items-center justify-center gap-2 ${
            filterType === 'harvest' ? 'bg-white text-[#1b7d36] shadow-sm' : 'text-stone-500'
          }`}
        >
          <ShoppingBag size={18} />
          {t.harvestMonth}
        </button>
      </div>

      {/* Results */}
      <div className="space-y-4">
        <h3 className="font-bold text-stone-800 flex items-center gap-2 px-1">
          <Filter size={18} className="text-[#1b7d36]" />
          {t.recommendedCrops} ({MONTHS[selectedMonth][language]})
        </h3>
        
        <div className="grid gap-4">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <Loader2 className="w-8 h-8 text-[#1b7d36] animate-spin" />
              <p className="text-stone-400 font-medium">{t.loading}</p>
            </div>
          ) : filteredCrops.length > 0 ? (
            filteredCrops.map((crop) => {
              const cropName = getLocalizedCropName(crop, language);
              const cropDetails = getLocalizedCropDetails(crop, language);
              const practices = (crop.practices || []).filter(p => {
                const title = getLocalizedPracticeTitle(p, language);
                return Boolean(title);
              });
              const isExpanded = expandedCropId === crop.id;

              return (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  key={crop.id}
                  className="bg-white p-5 rounded-[28px] shadow-sm border border-black/5 hover:border-[#1b7d36]/30 transition-colors space-y-4"
                >
                  <div className="flex items-start justify-between">
                    <div className="space-y-2 flex-1 pr-3">
                      <h4 className="text-lg font-bold text-stone-800">
                        {cropName}
                      </h4>
                      {cropDetails && (
                        <p className="text-stone-500 text-sm leading-relaxed">
                          {cropDetails}
                        </p>
                      )}
                      
                      <div className="flex flex-wrap gap-4 pt-2">
                        <div className="flex items-center gap-1.5">
                          <div className="w-2 h-2 rounded-full bg-emerald-500" />
                          <span className="text-xs font-semibold text-stone-400 uppercase tracking-wider">
                            {t.plantingMonth}: {(crop.plantingMonths || []).map(m => MONTHS[m][language]).join(', ')}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <div className="w-2 h-2 rounded-full bg-amber-500" />
                          <span className="text-xs font-semibold text-stone-400 uppercase tracking-wider">
                            {t.harvestMonth}: {(crop.harvestMonths || []).map(m => MONTHS[m][language]).join(', ')}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="bg-stone-50 p-3 rounded-2xl flex-shrink-0">
                      <Sprout className="text-[#1b7d36]" size={20} />
                    </div>
                  </div>

                  {/* Practices Accordion */}
                  {practices.length > 0 && (
                    <div className="pt-2 border-t border-stone-100">
                      <button
                        type="button"
                        onClick={() => setExpandedCropId(isExpanded ? null : crop.id)}
                        className="flex items-center justify-between w-full py-2 text-xs font-bold text-[#1b7d36] hover:text-emerald-800 transition-colors"
                      >
                        <span className="flex items-center gap-1.5">
                          <ListChecks size={15} />
                          {isExpanded 
                            ? (t.hidePractices || (language === 'te' ? 'సాగు పద్ధతులు దాచండి' : language === 'hi' ? 'खेती के तरीके छुपाएं' : 'Hide Practices'))
                            : `${t.viewPractices || (language === 'te' ? 'సాగు పద్ధతులు చూడండి' : language === 'hi' ? 'खेती के तरीके देखें' : 'View Practices')} (${practices.length})`
                          }
                        </span>
                        {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </button>

                      <AnimatePresence>
                        {isExpanded && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden space-y-2.5 pt-2"
                          >
                            {practices.map((p) => {
                              const pTitle = getLocalizedPracticeTitle(p, language);
                              const pDetails = getLocalizedPracticeDetails(p, language);

                              return (
                                <div key={p.id} className="p-3 bg-stone-50 rounded-2xl border border-stone-100 text-xs space-y-1">
                                  <div className="flex items-center justify-between font-bold text-stone-700">
                                    <span className="text-stone-900 font-semibold">{pTitle}</span>
                                    <span className="bg-emerald-100 text-emerald-800 text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 ml-2">
                                      {getDayOffsetLabel(p.dayOffset)}
                                    </span>
                                  </div>
                                  {pDetails && (
                                    <p className="text-stone-500 leading-relaxed text-[11px]">
                                      {pDetails}
                                    </p>
                                  )}
                                </div>
                              );
                            })}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  )}

                  {user && (
                    <div className="pt-2 border-t border-stone-100 flex items-center justify-between">
                      {isFollowing(crop.id) ? (
                        <div className="flex items-center gap-2">
                          <button 
                            onClick={() => handleUnfollow(crop.id)}
                            className="flex items-center gap-2 text-emerald-600 font-bold text-sm bg-emerald-50 px-4 py-2 rounded-xl"
                          >
                            <CheckCircle2 size={16} />
                            {t.following || (language === 'te' ? 'అనుసరిస్తున్నారు' : language === 'hi' ? 'फ़ॉलो कर रहे हैं' : 'Following')}
                          </button>
                          <button 
                            onClick={() => {
                              const followed = profile?.followedCrops?.find(c => c.cropId === crop.id);
                              if (followed) {
                                setEditingFollowedCrop(followed);
                                setSowingDate(followed.sowingDate);
                              }
                            }}
                            className="p-2 text-stone-400 hover:text-[#1b7d36] transition-colors bg-stone-50 rounded-xl"
                            title={t.editSowingDate}
                          >
                            <Calendar size={18} />
                          </button>
                        </div>
                      ) : (
                        <button 
                          onClick={() => {
                            setFollowingCrop(crop);
                            setSowingDate(new Date().toISOString().split('T')[0]);
                          }}
                          className="flex items-center gap-2 text-[#1b7d36] font-bold text-sm bg-stone-50 px-4 py-2 rounded-xl hover:bg-emerald-50 transition-colors"
                        >
                          <Plus size={16} />
                          {t.followCrop}
                        </button>
                      )}
                    </div>
                  )}
                </motion.div>
              );
            })
          ) : (
            <div className="text-center py-12 bg-white rounded-[32px] border border-dashed border-stone-200">
              <div className="bg-stone-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Info size={24} className="text-stone-300" />
              </div>
              <p className="text-stone-400 font-medium">
                {t.noCropsFoundCalendar || (
                  language === 'te' 
                    ? 'ఈ నెలకు సిఫార్సు చేసిన పంటలు ఏవీ లేవు.' 
                    : language === 'hi' 
                      ? 'इस महीने के लिए कोई अनुशंसित फसल नहीं मिली।' 
                      : 'No recommended crops found for this month.'
                )}
              </p>
            </div>
          )}
        </div>
      </div>

      <AnimatePresence>
        {(followingCrop || editingFollowedCrop) && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-[32px] w-full max-w-md p-6 shadow-2xl space-y-6"
            >
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold text-stone-800">
                  {editingFollowedCrop ? t.editSowingDate : t.followCrop}
                </h3>
                <button 
                  onClick={() => {
                    setFollowingCrop(null);
                    setEditingFollowedCrop(null);
                  }} 
                  className="text-stone-400"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="space-y-4">
                <div className="bg-emerald-50 p-4 rounded-2xl">
                  <p className="text-emerald-800 font-bold text-sm">
                    {editingFollowedCrop 
                      ? (crops.find(c => c.id === editingFollowedCrop.cropId)
                          ? getLocalizedCropName(crops.find(c => c.id === editingFollowedCrop.cropId)!, language)
                          : editingFollowedCrop.cropName)
                      : (followingCrop ? getLocalizedCropName(followingCrop, language) : '')
                    }
                  </p>
                  <p className="text-emerald-600 text-xs mt-1">
                    {editingFollowedCrop ? t.sowingDate : t.followCropConfirm}
                  </p>
                </div>

                <div>
                  <label className="text-xs font-bold text-stone-500 block mb-2">{t.selectSowingDate}</label>
                  <input 
                    type="date"
                    value={sowingDate}
                    onChange={(e) => setSowingDate(e.target.value)}
                    className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#1b7d36]"
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <button 
                  onClick={() => {
                    setFollowingCrop(null);
                    setEditingFollowedCrop(null);
                  }}
                  className="flex-1 py-3 rounded-2xl font-bold text-stone-500 bg-stone-100"
                >
                  {t.cancel}
                </button>
                <button 
                  onClick={editingFollowedCrop ? handleUpdateSowingDate : handleFollow}
                  className="flex-1 py-3 rounded-2xl font-bold text-white bg-[#1b7d36] shadow-md shadow-[#1b7d36]/20"
                >
                  {t.save}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
